'use client'

import dayjs, { Dayjs } from 'dayjs'
import React from 'react'

import { Swiper } from '../Swiper'
import {
  Arrow,
  ArrowContainer,
  Calendar,
  Container,
  Month,
  SwiperContainer,
} from './styles'

export interface CalendarDateSelectProps {
  /**
   * ID of this component
   * Switch id when using multiple CalendarDateSelect component in one page
   */
  id?: string

  /**
   * Class name
   */
  className?: string

  /**
   * Selected date in `YYYY-MM-DD` format
   */
  value?: string

  /**
   * Array of selectable dates in `YYYY-MM-DD` format
   */
  options: string[]

  /**
   * Callback triggered on value change
   */
  onChange?: (value: string) => void
}

interface DateOption {
  date: string
  format: (template?: string, locale?: 'en' | 'ja') => string
  isSelectable: boolean
  dayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6
  isWeekday: boolean
  isSaturday: boolean
  isSunday: boolean
}

const toDateOption = (date: string | Dayjs, options: string[]): DateOption => {
  const d = typeof date === 'string' ? dayjs(date) : date
  const dateStr = d.format('YYYY-MM-DD')
  const dayOfWeek = d.day() as DateOption['dayOfWeek']

  return {
    date: dateStr,
    format: (template?: string, locale: 'en' | 'ja' = 'en') =>
      d.locale(locale).format(template),
    isSelectable: options.includes(dateStr),
    dayOfWeek,
    isWeekday: dayOfWeek !== 0 && dayOfWeek !== 6,
    isSaturday: dayOfWeek === 6,
    isSunday: dayOfWeek === 0,
  }
}

const useSortedDateOptions = (options: string[]): DateOption[] => {
  return React.useMemo(() => {
    return options
      .sort((a, b) => (dayjs(a).isBefore(dayjs(b)) ? -1 : 1))
      .map((date) => toDateOption(date, options))
  }, [options])
}

const useDisplayDateOptions = (options: string[]): DateOption[] => {
  const dateOptions = useSortedDateOptions(options)

  return React.useMemo(() => {
    const lastIndex = dateOptions.length - 1
    const start = dayjs(dateOptions[0] ? dateOptions[0].date : dayjs()).startOf(
      'week',
    )

    const end = dayjs(
      dateOptions[lastIndex] ? dateOptions[lastIndex].date : dayjs(),
    ).endOf('week')

    const dayCount = end.diff(start, 'day') + 1

    return Array.from({ length: dayCount }).map((_, i) => {
      const date = start.add(i, 'day').format('YYYY-MM-DD')
      return toDateOption(date, options)
    })
  }, [dateOptions, options])
}

const useDisplayDateOptionsPerWeek = (
  dateOptions: DateOption[],
): DateOption[][] => {
  return React.useMemo(
    () =>
      dateOptions.reduce((acc, date, i) => {
        const weekIdx = Math.floor(i / 7)
        acc[weekIdx] = [...(acc[weekIdx] || []), date]
        return acc
      }, [] as DateOption[][]),
    [dateOptions],
  )
}

const useDisplayMonths = (dateOptions: DateOption[]): string[] => {
  return React.useMemo(
    () =>
      dateOptions
        .map((o) => dayjs(o.date).format('M月'))
        .filter((m, i, self) => self.indexOf(m) === i),
    [dateOptions],
  )
}

const useWeeklyCalendar = (dates: string[]) => {
  const [weekIndex, setWeekIndex] = React.useState(0)
  const items = useDisplayDateOptions(dates)
  const itemsPerWeek = useDisplayDateOptionsPerWeek(items)
  const currentItems = itemsPerWeek[weekIndex] as DateOption[]
  const currentMonths = useDisplayMonths(currentItems)

  return {
    // Full list of dates to display
    items,
    // Full list of dates by week
    itemsPerWeek,
    // Set week index of current view
    setWeekIndex,
    // Full list of dates of current week index
    currentItems,
    // List of months of current week index
    currentMonths,
  }
}

/**
 * Weekly calendar for date selection
 *
 * Note that this component uses the Swiper component internally,<br />
 * so we need to include `<SwiperStyles />` component
 * which is also exposed from this UI library.
 */
export const CalendarDateSelect = ({
  id,
  className,
  value,
  options,
  onChange,
}: CalendarDateSelectProps) => {
  const { itemsPerWeek, currentMonths, setWeekIndex } =
    useWeeklyCalendar(options)

  return (
    <Container className={className}>
      <Month>{currentMonths.join(' | ')}</Month>

      <SwiperContainer>
        <Swiper
          id={`calendar-date-select${id ? `-${id}` : ''}`}
          // TODO: storyshot fails when virtualization is enabled
          virtual={false}
          onSlideChange={({ activeIndex }) => setWeekIndex(activeIndex)}
          prevArrow={<Arrow />}
          nextArrow={<Arrow flip />}
          renderControls={({ prevClass, nextClass, prevArrow, nextArrow }) => (
            <>
              <ArrowContainer className={prevClass} left>
                {prevArrow}
              </ArrowContainer>
              <ArrowContainer className={nextClass} right>
                {nextArrow}
              </ArrowContainer>
            </>
          )}
          slides={itemsPerWeek.map((items, i) => (
            <Calendar.Slide key={i}>
              <Calendar.List>
                {items.map(
                  ({ date, format, isSelectable, isSaturday, isSunday }) => (
                    <Calendar.Item
                      key={date}
                      isSelectable={isSelectable}
                      isSelected={date === value}
                      isSaturday={isSaturday}
                      isSunday={isSunday}
                      onClick={() =>
                        // Trigger onChange only when selectable and not selected
                        isSelectable &&
                        date !== value &&
                        onChange &&
                        onChange(date)
                      }
                    >
                      <Calendar.Date>
                        <Calendar.DayOfWeek>
                          {format('ddd').toUpperCase()}
                        </Calendar.DayOfWeek>
                        <Calendar.Day>{format('D').toUpperCase()}</Calendar.Day>
                      </Calendar.Date>
                    </Calendar.Item>
                  ),
                )}
              </Calendar.List>
            </Calendar.Slide>
          ))}
        />
      </SwiperContainer>
    </Container>
  )
}
